import { RESET_DB } from 'actions/orm'
import {
  CREATE_SUCCESS,
  DELETE_SUCCESS,
  GET_ALL_SUCCESS,
  GET_ONE_SUCCESS,
  ResolvedAction,
  UPDATE_SUCCESS,
} from 'actions/redeemTemplates'
import { fk, Model, ModelType } from 'redux-orm'
import { ImageUrlsT } from 'utilities/types'

export type RedeemTemplateState = 'published' | 'unpublished' | 'archived'

export type RedeemType =
  | 'manual/item'
  | 'manual/amount'
  | 'manual/percent'
  | 'manual/fixed_price'
  | 'automatic/amount'
  | 'automatic/percent'
  | 'experience/hidden_menu'
  | 'experience/golden_ticket'
  | 'points/static'

export type RedeemTemplateSubtype =
  | 'item'
  | 'modifier'
  | 'purchase'
  | 'bogo/purchase'
  | 'bogo/item'
  | null

export type RedeemProductIdsType = {
  discounted?: string[]
  required?: string[]
  discounted_related?: string[]
  discounted_modifiers?: string[]
}

export type RedeemModifierIdsType = {
  discounted?: string[]
  required?: string[]
  discounted_related?: string[]
  discounted_modifiers?: string[]
}

export type RedeemProductIdsVenue = {
  digital?: RedeemProductIdsType
  instore?: RedeemProductIdsType
}

export type RedeemModifierIdsVenue = {
  digital?: RedeemModifierIdsType
  instore?: RedeemModifierIdsType
}

export type RedeemProductIds = {
  providerIds?: Record<string, RedeemProductIdsVenue>
  digital?: RedeemProductIdsType
  instore?: RedeemProductIdsType
}

export type RedeemModifierIds = {
  providerIds?: Record<string, RedeemModifierIdsVenue>
  digital?: RedeemModifierIdsType
  instore?: RedeemModifierIdsType
}

export type RedemptionVenue = 'all' | 'instore' | 'online'

export type ProductIdType =
  | 'required'
  | 'discounted'
  | 'discounted_related'
  | 'discounted_modifiers'

export type Images = {
  advertising_image?: ImageUrlsT
  index_image?: ImageUrlsT
  detail_image?: ImageUrlsT
}

type Upload = {
  id: number | null
  destroy?: boolean
}

export type MultiUseSettings = {
  schema: string
  end_condition: {
    max_issuances?: number
    active_window_duration: number
  }
  limit_condition?: {
    max_issuances_per_window: number
    window_duration: number
  }
}

export interface Fields {
  id: number
  hashid: string
  name: string
  description: string | null
  type: RedeemType
  subtype: RedeemTemplateSubtype
  state: RedeemTemplateState
  redemption_venue: RedemptionVenue
  discount: number | null
  price_cap: number | null
  discount_scope:
    | 'base_item'
    | 'base_item_plus_all_modifiers'
    | 'base_item_plus_required_modifiers'
    | 'required_modifiers'
    | 'free_base_item'
    | 'free_base_item_plus_all_modifiers'
    | 'free_base_item_plus_required_modifiers'
    | 'base_item_plus_discounted_modifiers'
    | 'free_base_item_plus_discounted_modifiers'
    | 'delivery_fee'
  discounted_check_section?: 'basket' | 'delivery_fee'
  minimum?: number
  perceived_value: number | null
  discounted_product_ids?: string[]
  discounted_modifier_ids?: string[]
  required_product_ids?: string[]
  granted_program_visible: boolean
  merchant_id: number
  created_at: string
  archived_at: string | null
  uses_coupon_codes: boolean
  uses_variable_codes: boolean
  available_coupon_code_count: number
  total_coupon_code_count: number
  unhides_category_ids?: number[]
  category_name?: string
  maximum?: number
  file_upload?: Upload
  images: Images
  url?: string
  long_description: string | null
  modifier_ids?: RedeemModifierIds
  number_of_points?: number
  points_experience_id?: number
  product_ids?: RedeemProductIds
  coupon_pool_ids?: number[]
  multi_use_settings?: MultiUseSettings | null
}

const createCleanTemplate = (): Partial<Fields> => ({
  description: null,
  discount: null,
  price_cap: null,
  perceived_value: null,
  discounted_product_ids: undefined,
  discounted_modifier_ids: undefined,
  required_product_ids: undefined,
  archived_at: null,
  unhides_category_ids: undefined,
  category_name: undefined,
  maximum: undefined,
  file_upload: undefined,
  images: {} as Images,
  url: undefined,
  long_description: null,
  modifier_ids: undefined,
  number_of_points: undefined,
  points_experience_id: undefined,
  product_ids: undefined,
  coupon_pool_ids: undefined,
  multi_use_settings: null,
})

export default class RedeemTemplate extends Model<
  // @ts-ignore
  typeof RedeemTemplate,
  Fields
> {
  static modelName: string = 'RedeemTemplate'

  static get fields(): any {
    return {
      merchant_id: fk({
        to: 'Merchant',
        as: 'merchant',
        relatedName: 'redeemTemplates',
      }),
    }
  }

  /* eslint-disable no-use-before-define */
  static reducer(
    action: ResolvedAction | { type: typeof RESET_DB },
    // @ts-ignore
    RedeemTemplate: ModelType<RedeemTemplate>
  ): void {
    /* eslint-enable no-use-before-define */
    switch (action.type) {
      case GET_ALL_SUCCESS:
        action.payload.data.redeem_templates.forEach(template => {
          RedeemTemplate.upsert(template)
        })
        break
      case GET_ONE_SUCCESS:
      case CREATE_SUCCESS:
        RedeemTemplate.upsert(action.payload.data.redeem_template)
        break
      case UPDATE_SUCCESS:
        RedeemTemplate.upsert({
          ...createCleanTemplate(),
          ...action.payload.data.redeem_template,
        })
        break
      case DELETE_SUCCESS: {
        const template = RedeemTemplate.withId(
          action.meta.previousAction.templateId
        )
        if (template) template.delete()
        break
      }
      case RESET_DB:
        RedeemTemplate.all().delete()
        break
      default:
        break
    }
  }
}
